import * as THREE from 'three';

/**
 * Build a cumulative arc-length table from an array of Three.js Vector3 points.
 * Called once per trace, result should be cached.
 * @param {THREE.Vector3[]} points - Trace polyline vertices in Three.js space
 * @returns {Float64Array} Cumulative distance at each vertex (starts at 0)
 */
export function buildDistanceTable(points) {
  const n = points.length;
  const table = new Float64Array(n);
  table[0] = 0;
  for (let i = 1; i < n; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    const dz = points[i].z - points[i - 1].z;
    table[i] = table[i - 1] + Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  return table;
}

/**
 * Binary search for the segment index where distTable[i] <= depth < distTable[i+1].
 */
function findSegment(distTable, depth) {
  let lo = 0;
  let hi = distTable.length - 1;
  if (depth <= distTable[0]) return 0;
  if (depth >= distTable[hi]) return Math.max(0, hi - 1);
  while (lo < hi - 1) {
    const mid = (lo + hi) >>> 1;
    if (distTable[mid] <= depth) lo = mid;
    else hi = mid;
  }
  return lo;
}

/**
 * Interpolate a 3D position and tangent along the trace at a given measured depth.
 * @param {Float64Array} distTable - From buildDistanceTable()
 * @param {THREE.Vector3[]} points - Trace polyline vertices
 * @param {number} depth - Measured depth along hole
 * @returns {{ position: THREE.Vector3, tangent: THREE.Vector3 }}
 */
export function interpolatePosition(distTable, points, depth) {
  const i = findSegment(distTable, depth);
  const j = Math.min(i + 1, points.length - 1);
  const segLen = distTable[j] - distTable[i];
  const t = segLen > 0 ? (depth - distTable[i]) / segLen : 0;

  const p0 = points[i];
  const p1 = points[j];

  const position = new THREE.Vector3(
    p0.x + (p1.x - p0.x) * t,
    p0.y + (p1.y - p0.y) * t,
    p0.z + (p1.z - p0.z) * t
  );

  const tangent = new THREE.Vector3(
    p1.x - p0.x,
    p1.y - p0.y,
    p1.z - p0.z
  );
  if (tangent.lengthSq() > 0) tangent.normalize();
  else tangent.set(0, -1, 0); // default downward if degenerate

  return { position, tangent };
}

/**
 * Interpolate a depth interval, returning placement data for an instanced cylinder.
 * @param {Float64Array} distTable
 * @param {THREE.Vector3[]} points
 * @param {number} fromDepth
 * @param {number} toDepth
 * @returns {{ start: THREE.Vector3, end: THREE.Vector3, midpoint: THREE.Vector3, length: number, quaternion: THREE.Quaternion }}
 */
export function interpolateInterval(distTable, points, fromDepth, toDepth) {
  const startResult = interpolatePosition(distTable, points, fromDepth);
  const endResult = interpolatePosition(distTable, points, toDepth);

  const midpoint = new THREE.Vector3()
    .addVectors(startResult.position, endResult.position)
    .multiplyScalar(0.5);

  const length = startResult.position.distanceTo(endResult.position);

  const dir = new THREE.Vector3()
    .subVectors(endResult.position, startResult.position);
  if (dir.lengthSq() > 0) dir.normalize();
  else dir.set(0, -1, 0);

  // CylinderGeometry is Y-up; rotate Y-axis to align with trace direction
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);

  return { start: startResult.position, end: endResult.position, midpoint, length, quaternion };
}

/**
 * Interpolate an interval and apply a radial offset for satellite channel placement.
 * When totalChannels <= 1 or offsetDistance === 0, behaves identically to interpolateInterval.
 *
 * @param {Float64Array} distTable
 * @param {THREE.Vector3[]} points
 * @param {number} fromDepth
 * @param {number} toDepth
 * @param {number} channelIndex - Which satellite slot (0-based)
 * @param {number} totalChannels - Total number of satellite slots
 * @param {number} offsetDistance - Radial distance from borehole axis
 */
export function interpolateIntervalWithOffset(
  distTable, points, fromDepth, toDepth, channelIndex, totalChannels, offsetDistance
) {
  const base = interpolateInterval(distTable, points, fromDepth, toDepth);
  if (totalChannels <= 1 || offsetDistance <= 0) return base;

  const tangent = new THREE.Vector3().subVectors(base.end, base.start);
  if (tangent.lengthSq() > 0) tangent.normalize();
  else tangent.set(0, -1, 0);

  const up = new THREE.Vector3(0, 1, 0);
  const perp = new THREE.Vector3().crossVectors(tangent, up);
  if (perp.lengthSq() < 0.001) {
    perp.crossVectors(tangent, new THREE.Vector3(1, 0, 0));
  }
  perp.normalize();

  const angle = (2 * Math.PI * channelIndex) / totalChannels;
  perp.applyAxisAngle(tangent, angle);
  perp.multiplyScalar(offsetDistance);

  base.start.add(perp.clone());
  base.end.add(perp.clone());
  base.midpoint.add(perp);

  return base;
}
